import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useCountry } from '../context/CountryContext';
import { AuthModal } from './auth/AuthModal';

export const Navbar = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { isAuthenticated, user, signOut } = useAuth();
  const { countries, selectedCountry, selectCountry } = useCountry();

  return (
    <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_replica-labs/artifacts/px3sdjtn_SC_Color%20change.png" 
              alt="Smart Connections" 
              className="h-40 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-sm font-bold text-navy hover:text-blue-600 transition-colors">
              Home
            </Link>
            
            <Link to="/shop" className="text-sm font-bold text-gray-600 hover:text-navy transition-colors">
              Shop
            </Link>

            {/* Products Dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1 text-sm font-bold text-gray-600 hover:text-navy transition-colors py-2">
                Products
                <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute top-full left-0 pt-0 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200">
                <div className="w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2">
                  <Link to="/shop" className="block px-4 py-3 text-sm text-navy hover:bg-gray-50 border-b border-gray-100">
                    <span className="font-semibold">Shop All</span>
                    <p className="text-xs text-gray-500">View all products</p>
                  </Link>
                  <Link to="/review-cards" className="block px-4 py-3 text-sm text-gray-600 hover:bg-gray-50">
                    <span className="font-semibold">Review Standees</span>
                    <p className="text-xs text-gray-500">Google & TripAdvisor standees</p>
                  </Link>
                  {/* COMMENTED OUT - Digital Cards - Will be used later */}
                  {/* <Link to="/digital-cards" className="block px-4 py-3 text-sm text-gray-600 hover:bg-gray-50">
                    <span className="font-semibold">Digital Cards</span>
                    <p className="text-xs text-gray-500">Smart business cards</p>
                  </Link> */}
                  {/* COMMENTED OUT - Enterprise - Will be used later */}
                  {/* <Link to="/enterprise" className="block px-4 py-3 text-sm text-gray-600 hover:bg-gray-50">
                    <span className="font-semibold">Enterprise</span>
                    <p className="text-xs text-gray-500">Solutions for teams</p>
                  </Link> */}
                </div>
              </div>
            </div>

            {/* Resources Dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1 text-sm font-bold text-gray-600 hover:text-navy transition-colors py-2">
                Resources
                <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute top-full left-0 pt-0 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200">
                <div className="w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2">
                  <Link to="/#how-it-works" className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
                    How It Works
                  </Link>
                  <Link to="/faq" className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
                    FAQ
                  </Link>
                  <Link to="/contact" className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>

            <Link to="/about" className="text-sm font-bold text-gray-600 hover:text-navy transition-colors">
              About
            </Link>
            {/* COMMENTED OUT - Artists - Will be used later */}
            {/* <Link to="/artist" className="text-sm font-bold text-gray-600 hover:text-navy transition-colors">
              Artists
            </Link> */}
          </div>

          {/* Right side buttons */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Country Selector */}
            {countries.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setIsCountryOpen(!isCountryOpen)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-navy border border-gray-200 rounded-lg hover:border-navy transition-colors"
                  data-testid="country-selector"
                >
                  <span className="text-lg">{selectedCountry?.flag || '🌍'}</span>
                  <span>{selectedCountry?.currency || 'USD'}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isCountryOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isCountryOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsCountryOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50 max-h-64 overflow-y-auto">
                      {countries.map((country) => (
                        <button
                          key={country.id}
                          onClick={() => {
                            selectCountry(country);
                            setIsCountryOpen(false);
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                            selectedCountry?.code === country.code ? 'bg-navy/5 text-navy' : 'text-gray-700'
                          }`}
                          data-testid={`country-option-${country.code}`}
                        >
                          <span className="text-xl">{country.flag}</span>
                          <span className="flex-1 text-left">{country.name}</span>
                          <span className="text-gray-500 font-mono text-xs">{country.currency}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {isAuthenticated ? (
              <>
                <Button 
                  onClick={() => navigate('/user/dashboard')}
                  className="bg-navy text-white hover:bg-navy/90 rounded-lg px-6 py-2"
                >
                  Dashboard
                </Button>
                <Button 
                  className="bg-gray-200 text-gray-700 hover:bg-gray-300 rounded-lg px-6 py-2"
                  onClick={() => {
                    signOut();
                    navigate('/');
                  }}
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <Button 
                onClick={() => setShowAuthModal(true)}
                className="bg-navy text-white hover:bg-navy/90 rounded-lg px-6 py-2"
              >
                Sign In
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 space-y-3 border-t border-gray-100">
            {/* Mobile Country Selector */}
            {countries.length > 0 && (
              <div className="pb-3 border-b border-gray-100">
                <p className="text-xs text-gray-500 mb-2">Select Currency</p>
                <select
                  value={selectedCountry?.code || ''}
                  onChange={(e) => {
                    const country = countries.find(c => c.code === e.target.value);
                    if (country) selectCountry(country);
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  {countries.map((country) => (
                    <option key={country.id} value={country.code}>
                      {country.flag} {country.name} ({country.currency})
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <Link to="/" className="block py-2 text-sm font-bold text-navy">
              Home
            </Link>
            <Link 
              to="/shop" 
              className="block py-2 text-sm font-bold text-gray-600"
              onClick={() => setIsMenuOpen(false)}
            >
              Shop All
            </Link>
            {/* COMMENTED OUT - Enterprise - Will be used later */}
            {/* <Link to="/enterprise" className="block py-2 text-sm font-bold text-gray-600">
              Enterprise & Team
            </Link> */}
            {/* COMMENTED OUT - Artist - Will be used later */}
            {/* <Link to="/artist" className="block py-2 text-sm font-bold text-gray-600">
              Artist
            </Link> */}
            <div>
              <button 
                onClick={() => setIsShopOpen(!isShopOpen)}
                className="flex items-center justify-between w-full py-2 text-sm text-gray-600"
              >
                <span>Products</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isShopOpen ? 'rotate-180' : ''}`} />
              </button>
              {isShopOpen && (
                <div className="pl-4 space-y-2 mt-2">
                  <Link to="/review-cards" className="block py-2 text-sm text-gray-500">
                    Review Standees
                  </Link>
                  {/* COMMENTED OUT - Digital Cards - Will be used later */}
                  {/* <Link to="/digital-cards" className="block py-2 text-sm text-gray-500">
                    Digital Cards
                  </Link> */}
                </div>
              )}
            </div>
            <Link to="/about" className="block py-2 text-sm font-bold text-gray-600">
              About Us
            </Link>
            <Link to="/contact" className="block py-2 text-sm font-bold text-gray-600">
              Contact Us
            </Link>
            {isAuthenticated ? (
              <div className="space-y-2">
                <Button 
                  onClick={() => {
                    navigate('/user/dashboard');
                    setIsMenuOpen(false);
                  }}
                  className="w-full mt-4 bg-navy text-white hover:bg-navy/90"
                >
                  Dashboard
                </Button>
                <Button 
                  className="w-full bg-gray-200 text-gray-700 hover:bg-gray-300"
                  onClick={() => {
                    signOut();
                    navigate('/');
                    setIsMenuOpen(false);
                  }}
                >
                  Sign Out
                </Button>
              </div>
            ) : (
              <Button 
                onClick={() => {
                  setShowAuthModal(true);
                  setIsMenuOpen(false);
                }}
                className="w-full mt-4 bg-navy text-white hover:bg-navy/90"
              >
                Sign In
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={(mode) => {
          setShowAuthModal(false);
          // After signup → land on Shop. After signin → land on dashboard.
          if (mode === 'signup') {
            navigate('/shop');
          } else {
            navigate('/user/dashboard');
          }
        }}
      />
    </nav>
  );
};
